import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import T from '@prisjakt/ui-translate';
import { Hideable } from '@prisjakt/ui/hideable';
import { Axis, Speed } from '@prisjakt/ui';
import { BodyMediumText, H3Text } from '@prisjakt/ui/text/variants';
import { PrimaryButton } from '@prisjakt/ui/button/variants';

import { useLocalStorage } from 'src/hooks/use-localstorage';
import actions from 'src/areas/search/actions/search';

import * as S from './styles';
import { NEW_SEARCH_INDEX_MARKETS } from '../../constants';

enum FeedbackId {
  RELEVANT = 1,
  NOT_RELEVANT = -1,
  DONT_KNOW = 0,
}

type FeedbackItem = {
  id: FeedbackId;
  translationId: string;
};

type RadioButtonProps = {
  item: FeedbackItem;
  onClick: (id: FeedbackItem) => void;
};

const RadioButton = ({ item, onClick }: RadioButtonProps) => {
  const onClickItem = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.preventDefault();
      onClick(item);
    },
    [onClick, item]
  );

  return (
    <S.Button onClick={onClickItem}>
      <T domain="search">{item.translationId}</T>
    </S.Button>
  );
};

type RadioButtonsProps = {
  items: FeedbackItem[];
  onItemSelected: (item: FeedbackItem) => void;
};

const RadioButtons = ({ items, onItemSelected }: RadioButtonsProps) => {
  return (
    <>
      {items.map(item => (
        <RadioButton
          key={item.id}
          item={item}
          onClick={(i: FeedbackItem) => {
            onItemSelected(i);
          }}
        />
      ))}
    </>
  );
};

type FeedbackProps = {
  query: string;
};

const RelevancyFeedback = (props: FeedbackProps) => {
  const { query } = props;

  const dispatch = useDispatch();
  const { market } = useAppContext();
  const [hide, setHide] = useState<boolean>(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);
  const [answer, setAnswer] = useState<FeedbackId | null>(null);

  const [_, setSearchFeedbackTimestamp] = useLocalStorage(
    'pj:searchRelevancyFeedbackTime',
    0
  );

  const newSearch = NEW_SEARCH_INDEX_MARKETS.includes(market.code);

  useEffect(() => {
    const handle =
      feedbackSubmitted &&
      setTimeout(() => {
        setSearchFeedbackTimestamp(new Date().valueOf());
        if (!newSearch && answer !== FeedbackId.NOT_RELEVANT) {
          setHide(true);
        }
      }, 3000);

    return () => {
      if (handle) {
        clearTimeout(handle);
      }
    };
  }, [answer, feedbackSubmitted, newSearch, setSearchFeedbackTimestamp]);

  const onFeedbackSubmit = useCallback(
    (feedback: FeedbackId) => {
      setFeedbackSubmitted(true);
      setAnswer(feedback);
      dispatch(actions.searchSubmitFeedback(feedback, query));
    },
    [dispatch, query]
  );

  return (
    <Hideable visible={!hide} speed={Speed.Instant}>
      <S.FeedbackBox>
        <S.Question gap={1}>
          {!feedbackSubmitted ? (
            <H3Text>
              <T domain="search">search.search-feedback-question</T>
            </H3Text>
          ) : (
            <>
              <H3Text>
                <T domain="search">search.search-feedback-answer</T>
              </H3Text>

              {newSearch && answer === FeedbackId.NOT_RELEVANT ? (
                <>
                  <BodyMediumText>
                    <T domain="search">relevancy.form-additional-help</T>
                  </BodyMediumText>
                  <PrimaryButton
                    container="a"
                    containerProps={{
                      href: 'https://prisjaktse.typeform.com/to/bHbmBPq1',
                      target: '_blank',
                    }}
                    style={{ alignSelf: 'center' }}
                  >
                    <T domain="search">relevancy.form-link-text</T>
                  </PrimaryButton>
                </>
              ) : null}
            </>
          )}
        </S.Question>
        <Hideable visible={!feedbackSubmitted} collapse={Axis.Y}>
          <S.ButtonContainer>
            <RadioButtons
              items={[
                {
                  id: FeedbackId.RELEVANT,
                  translationId: 'search.relevant-feedback',
                },
                {
                  id: FeedbackId.NOT_RELEVANT,
                  translationId: 'search.not-relevant-feedback',
                },
                {
                  id: FeedbackId.DONT_KNOW,
                  translationId: 'search.no-idea-feedback',
                },
              ]}
              onItemSelected={(i: FeedbackItem) => {
                setFeedbackSubmitted(true);
                onFeedbackSubmit(i.id);
              }}
            />
          </S.ButtonContainer>
        </Hideable>
      </S.FeedbackBox>
    </Hideable>
  );
};

export default RelevancyFeedback;
